import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import paymentService from '../services/paymentService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { todayInTimezone } from '../utils/datetime';
import '../styles/general.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(parseFloat(value) || 0);

/**
 * Register a payment against a document, saying which account the money
 * moved through and when.
 *
 * It does not know whether the document is a sale, a purchase or an expense:
 * the caller passes { id, number, total } and an onSubmit that performs the
 * call. What is genuinely shared lives here — the account list, the date
 * default, the validation and the mapping of field errors.
 *
 * The checks here duplicate the serializer's to give a fast answer. The
 * backend re-runs them and remains the guarantee.
 */
const RegisterPaymentModal = ({ document, isOpen, onClose, onPaid, onSubmit }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { timezone } = useContext(AuthContext);

  // todayInTimezone, not new Date().toISOString(): the latter is UTC, so a
  // user in Monterrey registering a payment after 18:00 would date it
  // tomorrow and drop it into the wrong accounting period.
  const today = todayInTimezone(timezone);

  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ monetary_account: '', paid_date: today });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const response = await paymentService.listPayableAccounts();
      setAccounts(response.data || []);
    } catch (err) {
      console.error('Error loading payable accounts:', err);
      notify.error(t('payments.failedToLoadAccounts'));
    }
  }, [notify, t]);

  useEffect(() => {
    if (!isOpen) return;
    setForm({ monetary_account: '', paid_date: today });
    setErrors({});
    loadAccounts();
  }, [isOpen, today, loadAccounts]);

  if (!isOpen || !document) return null;

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = () => {
    const found = {};

    if (!form.monetary_account) {
      found.monetary_account = t('payments.paymentAccountRequired');
    }
    if (!form.paid_date) {
      found.paid_date = t('payments.paidDateRequired');
    }

    return found;
  };

  const applyServerErrors = (err) => {
    const data = err?.response?.data;

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const mapped = {};
      Object.entries(data).forEach(([field, messages]) => {
        mapped[field] = Array.isArray(messages) ? messages.join(' ') : String(messages);
      });
      setErrors(mapped);
      return;
    }

    notify.error(t('payments.failedToRegister'));
  };

  const handleSave = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    try {
      await onSubmit(form);
      notify.success(t('payments.registered'));
      onPaid();
    } catch (err) {
      console.error('Error registering the payment:', err);
      applyServerErrors(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('payments.title')}>
      <div className="form-group">
        <p className="text-bold">{document.number}</p>
        <p className="text-muted">{formatCurrency(document.total)}</p>
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label" htmlFor="paymentAccount">
            {t('payments.paymentAccount')}
          </label>
          <select
            id="paymentAccount"
            value={form.monetary_account}
            onChange={handleChange('monetary_account')}
            className="form-select"
          >
            <option value="">{t('filters.none')}</option>
            {accounts.map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
          {errors.monetary_account && (
            <div className="form-error">{errors.monetary_account}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="paymentPaidDate">
            {t('payments.paidDate')}
          </label>
          <input
            id="paymentPaidDate"
            type="date"
            value={form.paid_date}
            onChange={handleChange('paid_date')}
            className="form-input"
          />
          {errors.paid_date && <div className="form-error">{errors.paid_date}</div>}
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
};

export default RegisterPaymentModal;
